
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { subscriptionService } from "@/services/subscriptionService";
import { toast } from "@/hooks/use-toast";
import { SubscriptionData, SubscriptionUpdateData } from "@/types/subscription";
import { useProfile } from "./useProfile";
import { useAuth } from "./useAuth";
import { supabase } from "@/integrations/supabase/client";

/**
 * Comprehensive hook for managing user subscriptions
 */
export function useSubscription(userId: string | null, phoneNumber: string | null) {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  if (!queryClient) {
    const error = new Error("❌ queryClient is not available");
    console.error(error);
    throw error;
  }

  const { checkSession } = useAuth();
  const profileHook = useProfile();

  // Fetch subscription data
  const {
    data: subscriptionData,
    isLoading: isLoadingSubscription,
    error: subscriptionError,
    refetch: refetchSubscription
  } = useQuery({
    queryKey: ["subscription", userId],
    queryFn: async (): Promise<SubscriptionData | null> => {
      if (!userId) return null;
      return await subscriptionService.fetchSubscriptionData(userId);
    },
    enabled: !!userId
  });

  // Update subscription mutation
  const updateSubscription = useMutation({
    mutationFn: async (data: SubscriptionUpdateData) => {
      if (!userId) throw new Error("No user ID provided");
      
      setIsLoading(true);
      try {
        // Verify session before proceeding
        await checkSession();
        
        // Ensure profile exists
        await profileHook.ensureProfileExists(userId, phoneNumber);
        
        // Update subscription - pass skipNotification as true to avoid SMS
        return await subscriptionService.updateSubscription(userId, data, true);
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      toast({
        title: "Subscription updated",
        description: "Your quote subscription has been updated successfully.",
      });
      
      queryClient.invalidateQueries({ queryKey: ["subscription", userId] });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating subscription",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    }
  });

  // Cancel subscription mutation
  const cancelSubscription = useMutation({
    mutationFn: async (subscriptionId: string | undefined) => {
      if (!userId || !subscriptionId) {
        toast({
          title: "No active subscription",
          description: "You don't have an active subscription to cancel.",
          variant: "destructive",
        });
        return false;
      }
      
      setIsLoading(true);
      try {
        // Cancel subscription
        return await subscriptionService.cancelSubscription(userId, subscriptionId);
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: () => {
      toast({
        title: "Unsubscribed",
        description: "You have been unsubscribed from quote delivery.",
      });
      
      queryClient.invalidateQueries({ queryKey: ["subscription", userId] });
      
      // Redirect to home page after unsubscribe
      window.location.href = '/';
    },
    onError: (error: any) => {
      toast({
        title: "Error unsubscribing",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    }
  });

  // Wrapper for unsubscribe function to use the subscription ID from data
  const unsubscribe = async () => {
    await cancelSubscription.mutateAsync(subscriptionData?.subscription?.id);
  };

  return {
    // Subscription data
    subscriptionData,
    isLoadingSubscription,
    subscriptionError,
    refetchSubscription,
    
    // Subscription actions
    updateSubscription,
    unsubscribe,
    
    // Loading state
    isLoading: isLoading || isLoadingSubscription || updateSubscription.isPending || cancelSubscription.isPending
  };
}
